import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/** Body for "AI SOC analyst" guide (AI in security operations). */
export function AiSocAnalystGuideBody() {
  return (
    <>
      <Lead>
        An AI SOC analyst is software that does the work a junior security analyst
        spends most of their day on, triaging alerts, gathering evidence, and
        writing up a verdict, at machine speed. It does not replace human analysts;
        it absorbs the repetitive volume so humans can focus on judgment. This guide
        explains what an AI SOC analyst is, what it does well, and where the human
        stays essential.
      </Lead>

      <GuideSection id="what-is-it" title="What is an AI SOC analyst?">
        <P>
          An <Term>AI SOC analyst</Term> (or AI SOC agent) is an AI system that
          performs front-line security-operations tasks: evaluating incoming alerts,
          enriching them with context, investigating, and producing a reasoned verdict
          with evidence. It is the analyst-shaped expression of the broader{' '}
          <GuideLink href="/guides/what-is-an-agentic-soc">agentic SOC</GuideLink>,
          an agent that works a queue the way a human analyst would, but far faster.
        </P>
      </GuideSection>

      <GuideSection id="what-it-does" title="What an AI SOC analyst does">
        <UL>
          <LI><Term>Alert triage</Term>: evaluate each alert, enrich it with context, and judge likely true vs false positive before a human sees it.</LI>
          <LI><Term>Investigation</Term>: gather evidence, decode artefacts, correlate signals across tools, and build a timeline.</LI>
          <LI><Term>Verdict &amp; write-up</Term>: deliver a conclusion with the evidence and reasoning, so a human can verify and decide.</LI>
          <LI><Term>Recommendation</Term>: suggest containment or response, which a human (or a gated workflow) approves.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="why" title="Why teams adopt it">
        <UL>
          <LI><Term>Alert fatigue</Term>: most SOC alerts are false positives; an AI analyst clears the noise so humans see the few that matter.</LI>
          <LI><Term>Talent shortage</Term>: it extends a small team&apos;s effective capacity without proportional headcount.</LI>
          <LI><Term>Speed</Term>: investigations that take a human 30 minutes can be drafted in seconds.</LI>
          <LI><Term>Consistency</Term>: every alert gets the same thorough first pass.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="human" title="Where the human stays essential">
        <P>
          The mature model is augmentation, not replacement, often called{' '}
          <Term>human-on-the-loop</Term>. The AI handles volume; humans own judgment,
          business context, novel threats, and any consequential action. Analysts
          shift from triaging alerts to <Term>supervising</Term> agent-led
          investigations, which is why <Term>transparency</Term> matters: an AI
          analyst that shows its evidence and citations can be trusted and overridden;
          a black box cannot.
        </P>
        <Callout title="Trust requires traceability">
          <p>
            An AI SOC analyst is only useful if a human can verify it. Insist on
            citations, an evidence trail, and a human-approval gate before any action
            touches your environment, especially for regulated teams.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse approaches the AI SOC analyst">
        <P>
          In LogPulse, notables raised by{' '}
          <GuideLink href="/guides/what-is-risk-based-alerting">risk-based alerting</GuideLink>{' '}
          are <Term>AI auto-investigated</Term>, and benign ones auto-close, so analysts
          see a handful of high-confidence cases with the evidence attached. Any change
          an AI agent proposes, a detection, rule, or response playbook, is created
          disabled until a human approves it. That is an{' '}
          <GuideLink href="/guides/what-is-an-agentic-soc">agentic SOC</GuideLink> with
          a human in the loop. See{' '}
          <GuideLink href="/agentic-soc">Agentic SOC</GuideLink> and{' '}
          <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink>.
        </P>
      </GuideSection>
    </>
  );
}
